import { Dispatch, FC, SetStateAction, useMemo, useReducer } from 'react';
import { Alert, Tabs } from '@silverfort/sf-components';
import { SaveFunc, StoreState } from './types';
import UserManagementSettingsContent from './components/UserManagementSettingsContent';
import {
    UserManagementSettingsContext,
    UserManagementSettingsContextType
} from './context/UserManagementSettingsContext';
import { useSaveSettings } from '../../hooks/useSaveSettings';
import { useSelector } from 'react-redux';
import useSearch from '../../hooks/useSearch';
import { useTabs } from '../../hooks/useTabs';
import { CATEGORIES, SUBCATEGORIES } from '../../common/categories';
import { fieldsReducer, initFieldsState, mapToSettings } from './reducer/userManagementSettingsReducer';
import { useHistory } from 'react-router-dom';
import { SnackAlertProvider } from '../../../UserManagement/context/SnackAlertProvider';
import { useVisibleTabs } from '../../hooks/lockedFeatures/useVisibleTabs';
import { useGetVisibleTabIds } from '../../hooks/lockedFeatures/useTabDisplay';

interface Props {
    saveFunc: SaveFunc;
}

const relevantSubcategories = [SUBCATEGORIES.ACCOUNT_MATCHING, SUBCATEGORIES.ENROLLMENT_SETTINGS];

const relevantTabs = relevantSubcategories.map((subcategory) => ({
    value: subcategory.ID,
    name: subcategory.NAME
}));

const UserManagementSettings: FC<Props> = ({ saveFunc }) => {
    const settings = useSelector((state: StoreState) => state.settings.settings);
    const [fields, dispatch] = useReducer(fieldsReducer, { settings }, initFieldsState);
    const visibleFields = useSearch(useVisibleTabs(fields));
    const visibleTabIds = useGetVisibleTabIds(relevantTabs.map((tab) => tab.value));
    const history = useHistory();
    // @ts-ignore - TS doesn't infer well the returned type
    const [selectedTab, setSelectedTab]: [string, Dispatch<SetStateAction<string>>] = useTabs(
        CATEGORIES.USER_MANAGEMENT,
        visibleFields
    );
    // @ts-ignore - TS doesn't infer well the returned type
    const [saveSettings]: [UserManagementSettingsContextType['saveSettings']] = useSaveSettings(
        saveFunc,
        settings,
        mapToSettings,
        fields,
        selectedTab,
        dispatch
    );

    const visibleTabs = useMemo(() => relevantTabs.filter((tab) => visibleTabIds.includes(tab.value)), [visibleTabIds]);

    if (!selectedTab) {
        return null;
    }
    return (
        <UserManagementSettingsContext.Provider value={{ saveSettings, dispatch, reducerState: fields, saveFunc }}>
            <SnackAlertProvider>
                <div className="w-full h-[calc(100vh-62px)] bg-sf-white flex flex-col">
                    <div className="h-16 px-6 flex items-center">
                        <span className="font-sf font-medium text-16 leading-[19.54px]">MFA User Management</span>
                    </div>
                    <div className="px-6 flex flex-col flex-1">
                        <Tabs
                            tabs={visibleTabs}
                            selectedTab={selectedTab}
                            onClick={(tabId) => {
                                history.push(`/Settings/${CATEGORIES.USER_MANAGEMENT.NAME}#${tabId}`);
                                setSelectedTab(tabId);
                            }}
                        />
                        <div className="flex-1">
                            <UserManagementSettingsContent selectedTab={selectedTab} />
                        </div>
                    </div>
                </div>
                <Alert />
            </SnackAlertProvider>
        </UserManagementSettingsContext.Provider>
    );
};

export default UserManagementSettings;
